import { Input as BaseInput } from '@chakra-ui/react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'

import theme from 'styles/theme'

export const SelectWrapper = styled.div<{ isDisabled?: boolean; isClickable?: boolean; zIndex: number }>`
  position: relative;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  pointer-events: ${({ isDisabled, isClickable }) => (isDisabled || !isClickable ? 'none' : 'all')};
  z-index: ${({ zIndex }) => zIndex};
`

export const BaseButton = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 54px;
  width: 100%;
  padding: 0 15px;
  background-color: ${theme.token.var('colors.green.a175')};
  border-radius: ${theme.token.var('borderRadius.small')};
  transition: all 150ms;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`

export const Button = styled.button<{
  isOpen?: boolean
  isInvalid?: boolean
}>`
  ${BaseButton};
  border-bottom-left-radius: ${({ isOpen }) => (isOpen ? 0 : theme.token.var('borderRadius.small'))};
  border-bottom-right-radius: ${({ isOpen }) => (isOpen ? 0 : theme.token.var('borderRadius.small'))};
  border: 1px solid ${({ isInvalid }) => (isInvalid ? theme.token.var('colors.red.a150') : 'transparent')};
`

export const Placeholder = styled.span<{ isIcon: boolean }>`
  color: ${theme.token.var('colors.green.a250')};
  margin-left: ${({ isIcon }) => (isIcon ? '30px' : 0)};
  font-weight: 300;
`

export const BaseOptions = css`
  left: 0;
  right: 0;
  position: absolute;
  z-index: 500;
  overflow-y: auto;
  color: ${theme.token.var('colors.green.a250')};
  border-bottom-left-radius: ${theme.token.var('borderRadius.small')};
  border-bottom-right-radius: ${theme.token.var('borderRadius.small')};
`

export const Options = styled.div`
  ${BaseOptions};
  background-color: ${theme.token.var('colors.green.a175')};
`

export const BaseOption = css`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 54px;
  padding: 7px 15px;
  cursor: pointer;
  border-top: 1px solid ${theme.token.var('colors.green.a200')};
  color: ${theme.token.var('colors.black')};
  font-size: ${theme.token.var('fontSizes.sm')};
  font-weight: 600;
`

export const Option = styled.li`
  ${BaseOption};
`

export const ArrowUp = styled.img`
  transform: rotate(180deg);
`

export const OptionRadio = styled.div<{ isChecked: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  background: ${({ isChecked }) =>
    isChecked ? theme.token.var('colors.green.a200') : theme.token.var('colors.green.a100')};
  margin-right: 12px;
`

export const Icon = styled.img`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 16px;
`

export const SharedInputStyles = css`
  border-radius: ${theme.token.var('borderRadius.small')};
  font-size: ${theme.token.var('fontSizes.sm')};
  background-color: ${theme.token.var('colors.green.a175')};
  width: 100%;

  // This is to prevent zoom in on iOS. According to this SO thread https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone/6394497
  // if font size in input is smaller than 16px, iOS will zoom in and therefore break layout. Using this bit of code we can detect iPhone and set there font size equal to 16px.
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    font-size: 16px;
  }

  &:focus {
    outline: none;
    box-shadow: none;
    border: none;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`

export const StyledInput = styled(BaseInput)<{
  isError: boolean
  paddingLeft: string
  paddingRight: string
  isSmaller: boolean
  disabled?: boolean
}>`
  ${SharedInputStyles};
  color: ${(props) => (props.isError ? theme.token.var('colors.red.a150') : theme.token.var('colors.black'))};
  border: ${(props) => (props.isError ? `1px solid ${theme.token.var('colors.red.a100')}` : 'none')};
  padding-left: ${({ paddingLeft }) => paddingLeft};
  padding-right: ${({ paddingRight }) => paddingRight};
  height: ${({ isSmaller }) => (isSmaller ? '36px' : '54px')};

  &::placeholder {
    color: ${(props) => (props.isError ? theme.token.var('colors.red.a150') : theme.token.var('colors.green.a200'))};
  }
`
